/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MedicalFacilityStatus } from 'src/graphql/generated/graphql.generated';
import {
  IdNameListItemRecord,
  IdNameListItemProps,
} from 'src/store/generated/id-name-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalFacilityProps {
  id: string;
  name: string;
  abbreviation: string;
  npi: string;
  providerNumber: string | null;
  sourceSystemId: number | null;
  masterDataSetId: number | null;
  firmType: string | null;
  hospitalType: string | null;
  hospitalSpecialization: string | null;
  website: string | null;
  defaultLandingZoneId: string | null;
  defaultFixedWingLandingZoneId: string | null;
  notes: string | null;
  stateSourceSystemId: string | null;
  nemsisCode: string | null;
  stateFacilityName: string | null;
  status: MedicalFacilityStatus | null;
  archiveNotes: string | null;
  archiveClearedDate: Date | null;
  communicationCenterCustomIdentifiers: List<IdNameListItemProps> | null;
  locationId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalFacilityRecord extends Record<MedicalFacilityProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  npi: null as any,
  providerNumber: null as any,
  sourceSystemId: null as any,
  masterDataSetId: null as any,
  firmType: null as any,
  hospitalType: null as any,
  hospitalSpecialization: null as any,
  website: null as any,
  defaultLandingZoneId: null as any,
  defaultFixedWingLandingZoneId: null as any,
  notes: null as any,
  stateSourceSystemId: null as any,
  nemsisCode: null as any,
  stateFacilityName: null as any,
  status: null as any,
  archiveNotes: null as any,
  archiveClearedDate: null as any,
  communicationCenterCustomIdentifiers: null as any,
  locationId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    npi: string;
    providerNumber: string | null;
    sourceSystemId: number | null;
    masterDataSetId: number | null;
    firmType: string | null;
    hospitalType: string | null;
    hospitalSpecialization: string | null;
    website: string | null;
    defaultLandingZoneId: string | null;
    defaultFixedWingLandingZoneId: string | null;
    notes: string | null;
    stateSourceSystemId: string | null;
    nemsisCode: string | null;
    stateFacilityName: string | null;
    status: MedicalFacilityStatus | null;
    archiveNotes: string | null;
    archiveClearedDate: string | number | Date | null;
    communicationCenterCustomIdentifiers: any;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalFacilityRecord {
    if (json == null) {
      throw new Error('MedicalFacilityRecord.fromJS: parameter cannot be null');
    }
    return new MedicalFacilityRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'MedicalFacilityJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'MedicalFacilityJson.abbreviation'),
      npi: getFromOrThrow(json, (j) => j.npi, 'MedicalFacilityJson.npi'),
      providerNumber: json.providerNumber,
      sourceSystemId: json.sourceSystemId,
      masterDataSetId: json.masterDataSetId,
      firmType: json.firmType,
      hospitalType: json.hospitalType,
      hospitalSpecialization: json.hospitalSpecialization,
      website: json.website,
      defaultLandingZoneId: json.defaultLandingZoneId,
      defaultFixedWingLandingZoneId: json.defaultFixedWingLandingZoneId,
      notes: json.notes,
      stateSourceSystemId: json.stateSourceSystemId,
      nemsisCode: json.nemsisCode,
      stateFacilityName: json.stateFacilityName,
      status: json.status,
      archiveNotes: json.archiveNotes,
      archiveClearedDate:
        json.archiveClearedDate != null ? new Date(json.archiveClearedDate) : null,
      communicationCenterCustomIdentifiers:
        json.communicationCenterCustomIdentifiers != null
          ? List<IdNameListItemRecord>(
              json.communicationCenterCustomIdentifiers.map(
                (o: any) => new IdNameListItemRecord(o),
              ),
            )
          : null,
      locationId: json.locationId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    npi: string;
    providerNumber: string | null;
    sourceSystemId: number | null;
    masterDataSetId: number | null;
    firmType: string | null;
    hospitalType: string | null;
    hospitalSpecialization: string | null;
    website: string | null;
    defaultLandingZoneId: string | null;
    defaultFixedWingLandingZoneId: string | null;
    notes: string | null;
    stateSourceSystemId: string | null;
    nemsisCode: string | null;
    stateFacilityName: string | null;
    status: MedicalFacilityStatus | null;
    archiveNotes: string | null;
    archiveClearedDate: string | number | Date | null;
    communicationCenterCustomIdentifiers: any;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalFacilityRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalFacilityRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalFacilityRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'MedicalFacilityJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'MedicalFacilityJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      npi: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.npi, 'MedicalFacilityJson.npi'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      providerNumber: tryOrDefault(
        () => json.providerNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceSystemId: tryOrDefault(
        () => json.sourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      masterDataSetId: tryOrDefault(
        () => json.masterDataSetId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firmType: tryOrDefault(
        () => json.firmType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hospitalType: tryOrDefault(
        () => json.hospitalType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hospitalSpecialization: tryOrDefault(
        () => json.hospitalSpecialization,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      website: tryOrDefault(
        () => json.website,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultLandingZoneId: tryOrDefault(
        () => json.defaultLandingZoneId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultFixedWingLandingZoneId: tryOrDefault(
        () => json.defaultFixedWingLandingZoneId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stateSourceSystemId: tryOrDefault(
        () => json.stateSourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nemsisCode: tryOrDefault(
        () => json.nemsisCode,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stateFacilityName: tryOrDefault(
        () => json.stateFacilityName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () => json.status,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      archiveNotes: tryOrDefault(
        () => json.archiveNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      archiveClearedDate: tryOrDefault(
        () => (json.archiveClearedDate != null ? new Date(json.archiveClearedDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterCustomIdentifiers: tryOrDefault(
        () =>
          json.communicationCenterCustomIdentifiers != null
            ? List<IdNameListItemRecord>(
                json.communicationCenterCustomIdentifiers.map(
                  (o: any) => new IdNameListItemRecord(o),
                ),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalFacilityProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalFacilityRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalFacilityViewModelBase implements MedicalFacilityProps {
  protected readonly _props: MedicalFacilityProps;
  private _json?: MedicalFacilityProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get npi(): string {
    return this._props.npi;
  }
  get providerNumber(): string | null {
    return this._props.providerNumber;
  }
  get sourceSystemId(): number | null {
    return this._props.sourceSystemId;
  }
  get masterDataSetId(): number | null {
    return this._props.masterDataSetId;
  }
  get firmType(): string | null {
    return this._props.firmType;
  }
  get hospitalType(): string | null {
    return this._props.hospitalType;
  }
  get hospitalSpecialization(): string | null {
    return this._props.hospitalSpecialization;
  }
  get website(): string | null {
    return this._props.website;
  }
  get defaultLandingZoneId(): string | null {
    return this._props.defaultLandingZoneId;
  }
  get defaultFixedWingLandingZoneId(): string | null {
    return this._props.defaultFixedWingLandingZoneId;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get stateSourceSystemId(): string | null {
    return this._props.stateSourceSystemId;
  }
  get nemsisCode(): string | null {
    return this._props.nemsisCode;
  }
  get stateFacilityName(): string | null {
    return this._props.stateFacilityName;
  }
  get status(): MedicalFacilityStatus | null {
    return this._props.status;
  }
  get archiveNotes(): string | null {
    return this._props.archiveNotes;
  }
  get archiveClearedDate(): Date | null {
    return this._props.archiveClearedDate;
  }
  get communicationCenterCustomIdentifiers(): List<IdNameListItemProps> | null {
    return this._props.communicationCenterCustomIdentifiers;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isApproved() {
    return this._props.status === MedicalFacilityStatus.Approved;
  }
  public get isArchived() {
    return this._props.status === MedicalFacilityStatus.Archived;
  }
  public get isDuplicate() {
    return this._props.status === MedicalFacilityStatus.Duplicate;
  }
  public get isPendingApproval() {
    return this._props.status === MedicalFacilityStatus.PendingApproval;
  }
  public get isPendingArchive() {
    return this._props.status === MedicalFacilityStatus.PendingArchive;
  }

  public isAnyStatus(...medicalFacilityStatus: MedicalFacilityStatus[]) {
    return this._props.status != null && medicalFacilityStatus.includes(this._props.status);
  }

  constructor(props: MedicalFacilityProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        npi: this._props.npi,
        providerNumber: this._props.providerNumber,
        sourceSystemId: this._props.sourceSystemId,
        masterDataSetId: this._props.masterDataSetId,
        firmType: this._props.firmType,
        hospitalType: this._props.hospitalType,
        hospitalSpecialization: this._props.hospitalSpecialization,
        website: this._props.website,
        defaultLandingZoneId: this._props.defaultLandingZoneId,
        defaultFixedWingLandingZoneId: this._props.defaultFixedWingLandingZoneId,
        notes: this._props.notes,
        stateSourceSystemId: this._props.stateSourceSystemId,
        nemsisCode: this._props.nemsisCode,
        stateFacilityName: this._props.stateFacilityName,
        status: this._props.status,
        archiveNotes: this._props.archiveNotes,
        archiveClearedDate: this._props.archiveClearedDate,
        communicationCenterCustomIdentifiers: this._props.communicationCenterCustomIdentifiers,
        locationId: this._props.locationId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalFacilityEntities = (state: AppState) =>
  state.entityStores.medicalFacility.entities;

export const selectMedicalFacilityById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacility.entities.get(id);

export const selectMedicalFacilityMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacility.meta.get(id);

export const selectMedicalFacilityAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalFacilityById(id),
    selectMedicalFacilityMetadataById(id),
    (medicalFacility, meta) => ({ medicalFacility, meta }),
  );

export function selectMedicalFacilityEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalFacilityEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalFacilityRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalFacilityEntitiesByName(name: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByNpi(npi: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.npi === npi).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByProviderNumber(providerNumber: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.providerNumber === providerNumber).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesBySourceSystemId(sourceSystemId: number) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.sourceSystemId === sourceSystemId).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByMasterDataSetId(masterDataSetId: number) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.masterDataSetId === masterDataSetId).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByFirmType(firmType: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.firmType === firmType).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByHospitalType(hospitalType: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.hospitalType === hospitalType).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByHospitalSpecialization(
  hospitalSpecialization: string,
) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.hospitalSpecialization === hospitalSpecialization).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByWebsite(website: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.website === website).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByDefaultLandingZoneId(defaultLandingZoneId: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.defaultLandingZoneId === defaultLandingZoneId).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByDefaultFixedWingLandingZoneId(
  defaultFixedWingLandingZoneId: string,
) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities
      .filter((e) => e.defaultFixedWingLandingZoneId === defaultFixedWingLandingZoneId)
      .valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByNotes(notes: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByStateSourceSystemId(stateSourceSystemId: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.stateSourceSystemId === stateSourceSystemId).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByNemsisCode(nemsisCode: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.nemsisCode === nemsisCode).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByStateFacilityName(stateFacilityName: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.stateFacilityName === stateFacilityName).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByStatus(status: MedicalFacilityStatus) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByArchiveNotes(archiveNotes: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.archiveNotes === archiveNotes).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByArchiveClearedDate(archiveClearedDate: Date) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.archiveClearedDate === archiveClearedDate).valueSeq(),
  );
}

export function selectMedicalFacilityEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}
//#endregion Selectors
